import { useState } from 'react';
import { useReduxState } from 'hooks';
import { EmptyIndicator } from 'components/common';
import { Box } from 'components/common/box';
import { AreaChart } from 'components/common/charts';
import { AnalyticsChartLegend } from '.';

interface Props<T extends readonly string[]> {
  areaLabels: T;
  colors: Record<T[number], string>;
  data: ({ xLabel: string | number } & Record<T[number], string | number>)[];
  legendDirection?: 'row' | 'column';
  title: string;
}

const AnalyticsAreaChart = <T extends readonly string[]>({
  areaLabels,
  colors,
  data,
  legendDirection = 'row',
  title
}: Props<T>) => {
  const { messages, theme } = useReduxState();

  const [visibleLines, setVisibleLines] = useState<Set<string>>(
    new Set([...areaLabels])
  );

  const toggleLine = (label: string) => {
    setVisibleLines((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(label)) newSet.delete(label);
      else newSet.add(label);
      return newSet;
    });
  };

  // Filter data and colors based on visible lines
  const visibleLineLabels: string[] = areaLabels.filter((label) =>
    visibleLines.has(label)
  );

  // find max y value
  let yMaxValue = 0;
  data?.forEach((item) => {
    const yValue = visibleLineLabels.reduce(
      (sum, label) => sum + item[label],
      0
    );
    if (yValue > yMaxValue) yMaxValue = yValue;
  });

  return (
    <Box padding={0} title={title}>
      {!data.length ? (
        <EmptyIndicator
          icon="chart-line"
          title={messages.analyticsChooseDateRange}
        />
      ) : (
        <>
          <div className="content">
            <div className="chart">
              <AreaChart
                accessibilityLayer={true}
                data={data}
                xAxisDataKey="xLabel"
                lineColors={Object.values(colors) as string[]}
                yAxisDataKey={areaLabels as unknown as string[]}
                yAxisDataKeysVisible={visibleLineLabels as string[]}
                yAxisDomain={yMaxValue < 12 ? [0, 12] : [0, 'auto']}
                stacked={true}
              />
            </div>
          </div>

          <div className="legends">
            <AnalyticsChartLegend
              colors={colors}
              labels={areaLabels}
              labelsVisible={visibleLines}
              legendDirection={legendDirection}
              onLegendItemClick={toggleLine}
            />
          </div>
        </>
      )}

      <style jsx>{`
        .content {
          display: flex;
        }
        .chart {
          flex-grow: 1;
          flex-shrink: 1;
          padding: 20px;
        }
        .side {
          border-left: 1px solid ${theme.lightGrey};
          padding: 20px;
        }
        .legends {
          border-top: 1px solid ${theme.lightGrey};
          padding: 20px;
        }
      `}</style>
    </Box>
  );
};

export default AnalyticsAreaChart;
